import React from 'react';
import { StyleSheet, Text, View } from 'react-native';
import { spacing } from '../../../shared/theme/spacing'
import { typography } from '../../../shared/theme/typography';
import { ticketColors } from '../../../shared/theme/ticketColors';
import type { OnboardingWizardStepDef, OnboardingWizardStepId } from './onboardingWizardConfig';
import { protoProgress } from './protoProgress';

type Props = {
  steps: OnboardingWizardStepDef[];
  currentIndex: number;
  /** Preferir stepId: evita desync si el índice no matchea el paso visible. */
  stepId?: OnboardingWizardStepId;
  variant: 'horizontal' | 'vertical';
};

export function OnboardingWizardStepper({ steps, currentIndex, stepId, variant }: Props) {
  const resolvedId =
    stepId ?? steps[currentIndex]?.id ?? steps[0]?.id ?? ('business-type' as OnboardingWizardStepId);
  const { current, total } = protoProgress(resolvedId);

  if (variant === 'vertical') {
    return (
      <View style={styles.verticalRoot} accessibilityRole="tablist">
        {steps.map((step, idx) => {
          const active = idx === currentIndex;
          const done = idx < currentIndex;
          return (
            <View key={step.id} style={styles.verticalRow} accessibilityRole="tab" accessibilityState={{ selected: active }}>
              <View style={styles.verticalRail}>
                <View style={[styles.stepDot, active && styles.stepDotActive, done && styles.stepDotDone]}>
                  <Text
                    style={[
                      styles.stepDotText,
                      active && styles.stepDotTextActive,
                      done && styles.stepDotTextDone,
                    ]}
                  >
                    {done ? '✓' : idx + 1}
                  </Text>
                </View>
                {idx < steps.length - 1 ? (
                  <View style={[styles.verticalConnector, done && styles.verticalConnectorDone]} />
                ) : null}
              </View>
              <View style={styles.verticalCopy}>
                <Text style={[styles.verticalLabel, active && styles.verticalLabelActive]}>{step.label}</Text>
                {active ? <Text style={styles.verticalHint}>{step.title}</Text> : null}
              </View>
            </View>
          );
        })}
      </View>
    );
  }

  // Proto: "1 / 3" + barra. Fill por flex (más fiable que width % en RN web).
  return (
    <View
      key={`onboarding-progress-${resolvedId}-${current}`}
      style={styles.horizontalRoot}
      accessibilityRole="progressbar"
      accessibilityValue={{ min: 0, max: total, now: current }}
      accessibilityLabel={`Paso ${current} de ${total}`}
      testID="onboarding.progress"
    >
      <Text style={styles.progressCount} testID="onboarding.progress.label">
        {current} / {total}
      </Text>
      <View style={styles.progressTrack}>
        <View style={[styles.progressFill, { flex: current }]} />
        <View style={{ flex: Math.max(0, total - current) }} />
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  horizontalRoot: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
    marginBottom: spacing.md,
  },
  progressCount: {
    ...typography.caption,
    color: ticketColors.ink3,
    fontWeight: '700',
    fontVariant: ['tabular-nums'],
    minWidth: 36,
  },
  progressTrack: {
    flex: 1,
    flexDirection: 'row',
    height: 3,
    backgroundColor: ticketColors.brandSoft,
    borderRadius: 999,
    overflow: 'hidden',
  },
  progressFill: {
    height: '100%',
    backgroundColor: ticketColors.gold,
    borderRadius: 999,
  },
  verticalRoot: {
    gap: spacing.md,
  },
  verticalRow: {
    flexDirection: 'row',
    gap: spacing.md,
  },
  verticalRail: {
    alignItems: 'center',
    width: 28,
  },
  verticalConnector: {
    flex: 1,
    width: 2,
    minHeight: 20,
    marginTop: spacing.xs,
    backgroundColor: ticketColors.rule,
    borderRadius: 1,
  },
  verticalConnectorDone: {
    backgroundColor: ticketColors.gold,
    opacity: 0.45,
  },
  verticalCopy: {
    flex: 1,
    paddingBottom: spacing.sm,
  },
  verticalLabel: {
    ...typography.body,
    color: ticketColors.ink2,
    fontWeight: '600',
  },
  verticalLabelActive: {
    color: ticketColors.ink,
  },
  verticalHint: {
    ...typography.caption,
    color: ticketColors.ink3,
    marginTop: spacing.xs,
  },
  stepDot: {
    width: 28,
    height: 28,
    borderRadius: 14,
    borderWidth: 1,
    borderColor: ticketColors.ruleSoft,
    backgroundColor: ticketColors.paper2,
    alignItems: 'center',
    justifyContent: 'center',
  },
  stepDotActive: {
    borderColor: ticketColors.gold,
    backgroundColor: ticketColors.brandSoft,
  },
  stepDotDone: {
    borderColor: ticketColors.gold,
    backgroundColor: ticketColors.gold,
  },
  stepDotText: {
    ...typography.caption,
    color: ticketColors.ink2,
    fontWeight: '700',
  },
  stepDotTextActive: {
    color: ticketColors.ink,
  },
  stepDotTextDone: {
    color: '#FFFFFF',
  },
});
